import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { gossipArticles } from "@/mocks/gossipArticles";
import Seo from "@/components/base/Seo";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import HotRanking from "@/components/feature/HotRanking";
import NotFound from "@/pages/NotFound";
import { formatCount } from "@/utils/format";
import { SITE_URL } from "@/utils/site";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = gossipArticles.find((item) => item.slug === slug);
  const [copied, setCopied] = useState(false);

  if (!article) {
    return <NotFound />;
  }

  const related = gossipArticles
    .filter((item) => item.categorySlug === article.categorySlug && item.slug !== article.slug)
    .slice(0, 3);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${SITE_URL}/article/${article.slug}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: article.categoryName,
        item: `${SITE_URL}/category/${article.categorySlug}`,
      },
      { "@type": "ListItem", position: 3, name: article.title, item: `${SITE_URL}/article/${article.slug}` },
    ],
  };

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    articleSection: article.categoryName,
    keywords: article.tags.join(","),
    inLanguage: "zh-CN",
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
    image: article.cover,
    author: { "@type": "Organization", name: article.author },
    publisher: { "@type": "Organization", name: "瓜料动态", url: SITE_URL },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/article/${article.slug}` },
  };

  return (
    <>
      <Seo
        title={`${article.title} | 瓜料动态`}
        description={article.summary.slice(0, 150)}
        keywords={`${article.tags.join(",")},瓜料动态,瓜料动态最新进展,瓜料动态今日更新`}
        path={`/article/${article.slug}`}
        ogType="article"
        jsonLd={[breadcrumbLd, articleLd]}
      />
      <Layout>
        <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: article.categoryName, to: `/category/${article.categorySlug}` },
              { label: "正文" },
            ]}
          />

          <div className="mt-8 grid gap-10 lg:grid-cols-[1.8fr_1fr]">
            <article>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-label text-primary-700">
                <i className="ri-price-tag-3-line" /> {article.categoryName}
              </span>
              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                {article.title}
              </h1>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line" /> {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" /> {article.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line" /> {formatCount(article.readCount)} 阅读
                </span>
                <span className="flex items-center gap-1.5 text-primary-600">
                  <i className="ri-fire-line" /> 热度 {article.hotScore}
                </span>
              </div>

              <div className="mt-6 h-64 w-full overflow-hidden rounded-lg md:h-[420px]">
                <img
                  src={article.cover}
                  alt={`${article.title} - 瓜料动态${article.categoryName}`}
                  title={`${article.title} - 瓜料动态最新进展`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <p className="mt-8 border-l-2 border-primary-400 bg-background-100 px-5 py-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                {article.summary}
              </p>

              <div className="mt-8 space-y-5">
                {article.content.map((paragraph) => (
                  <p key={paragraph.slice(0, 12)} className="text-sm leading-[1.9] text-foreground-700 md:text-base">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-label text-secondary-900">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="text-sm text-foreground-600">把这条瓜料分享出去：</span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-foreground-800"
                >
                  <i className={copied ? "ri-check-line" : "ri-link"} />
                  {copied ? "链接已复制" : "复制链接"}
                </button>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-price-tag-3-line" /> 查看{article.categoryName}
                </Link>
              </div>

              {related.length > 0 && (
                <section className="mt-12">
                  <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
                  <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {related.map((item) => (
                      <ArticleCard key={item.slug} article={item} />
                    ))}
                  </div>
                </section>
              )}
            </article>

            <aside className="space-y-8">
              <HotRanking limit={8} />
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">最新更新</h2>
                <div className="mt-3 divide-y divide-background-200">
                  {article &&
                    gossipArticles.slice(0, 5).map((item) => (
                      <Link key={item.slug} to={`/article/${item.slug}`} className="group block py-3">
                        <span className="block text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-500">{item.relativeTime}</span>
                      </Link>
                    ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </Layout>
    </>
  );
}